<template>
  <div class="promo-card">
    <div
      class="promo-card__image"
      :class="
        showPromoImage
          ? 'promo-card__image--asset'
          : `promo-card__image--${promotion.type}`
      "
    >
      <img
        v-if="showPromoImage"
        class="promo-card__img"
        :src="promoImageSrc"
        alt=""
        @error="imageFailed = true"
      />
      <div v-else class="promo-card__image-object"></div>
    </div>

    <div class="promo-card__body">
      <h4 class="promo-card__title">{{ displayTitle }}</h4>
      <div
        v-if="descriptionHtml"
        class="promo-card__description"
        v-html="descriptionHtml"
      />
      <div class="promo-card__footer">
        <span class="promo-card__points">{{ displayPointsLabel }}</span>
        <div class="promo-card__actions">
          <router-link
            v-if="promotion.slug"
            class="promo-card__details-link"
            :to="{ name: 'portalPromotionDetail', params: { slug: promotion.slug } }"
          >
            {{ $t("portalHome.promotions.detailsLink") }}
          </router-link>
          <button
            type="button"
            class="promo-card__cta"
            :class="{ 'promo-card__cta--signed-in': promotion.requiresSignIn && promotion.signedIn }"
            :disabled="isCtaDisabled"
            @click="handleCtaClick"
          >
            {{ ctaLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { defineEmits, defineProps } from "vue";
import { useI18n } from "vue-i18n";
import { resolveBundledAssetUrl } from "@/utils/resolveBundledAssetUrl";

const props = defineProps({
  promotion: { type: Object, required: true },
  signingIn: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["sign-in"]);

const { t } = useI18n();

const promoImageSrc = computed(() =>
  resolveBundledAssetUrl(props.promotion.imageSrc)
);
const imageFailed = ref(false);
const showPromoImage = computed(() => promoImageSrc.value && !imageFailed.value);

const displayTitle = computed(() => {
  if (props.promotion.title) {
    return props.promotion.title;
  }

  return props.promotion.titleKey ? t(props.promotion.titleKey) : "";
});

const descriptionHtml = computed(() => {
  if (props.promotion.description) {
    return props.promotion.description;
  }

  if (props.promotion.longDescription) {
    return props.promotion.longDescription;
  }

  return props.promotion.descriptionKey ? t(props.promotion.descriptionKey) : "";
});

const displayPointsLabel = computed(() => {
  if (props.promotion.pointsLabel) {
    return props.promotion.pointsLabel;
  }

  if (props.promotion.pointsLabelKey) {
    return t(props.promotion.pointsLabelKey);
  }

  const value = props.promotion.promotionValue;
  if (props.promotion.amountType === "multiply" && value != null) {
    return t("portalHome.promotions.pointsLabel.multiply", { value });
  }

  if (props.promotion.amountType === "add" && value != null) {
    return t("portalHome.promotions.pointsLabel.add", { value });
  }

  return "";
});

const ctaLabel = computed(() => {
  if (props.promotion.requiresSignIn && !props.promotion.signedIn) {
    return t("portalHome.promotions.signInButton");
  }

  if (props.promotion.requiresSignIn && props.promotion.signedIn) {
    return t("portalHome.promotions.signedInButton");
  }

  return t("portalHome.promotions.cardButton");
});

const isCtaDisabled = computed(
  () =>
    props.signingIn ||
    (props.promotion.requiresSignIn && props.promotion.signedIn),
);

const handleCtaClick = () => {
  if (props.promotion.requiresSignIn && !props.promotion.signedIn) {
    emit("sign-in", props.promotion.id);
  }
};

watch(promoImageSrc, () => {
  imageFailed.value = false;
});
</script>

<style scoped lang="scss">
@import "@/styles/portal-home/promotion-card";
</style>
